import * as React from 'react';
import Card from '../../layout/card/Card';
import { css } from '@emotion/core';
import CardHeader from '../../layout/card/CardHeader';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import ProgressCircle from '../../loading/ProgressCircle';
import { COLORS } from '../../globalStyle/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import Spacer from '../../layout/Spacer';
import { Link } from 'react-router-dom';
import { btnStyle } from '../../forms/styles/button';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import Msg from '../../localization/Msg';
import {
  ctaMsgForProgress,
  subtitleMsgForProgress,
  colorForProgress,
  routeForProgress,
  requirementsMsgFor,
  requirementsMsgSubstitutionsFor,
} from './module';
import { DevPlan } from './models';
import { Routes } from '../../navigation/routes';
import { useBrowser } from '../../language/useBrowser';
import { pathCombine } from '../../core/api/module';
import { BaseUrl } from '../../core/api/endpoints';


const styles = {
  header:             css`
    margin-bottom: 20px;
  `,
  content:               css`
    align-items: stretch;
    min-height: 150px;
    flex-basis: 150px;
  `,
  leftSide:  css`
    color: ${COLORS.GREEN};
    text-decoration: none;
    align-items: center;
  `,
  rightSide:      [
    FLEX_COLUMN,
    css`
      flex-grow: 1;
    `,
  ],
  fullWidth:          css`
    width: 100%;
  `,
  subtitle:           css`
    font-weight: bold; 
    font-size: 16px;
    margin-bottom: 10px;
  `,
  reqText:            css`
    margin-bottom: 10px;
  `,
  progress:           css`
    flex-basis: 130px; 
    flex-shrink: 0; 
    width: 130px; 
    height: 130px;
    margin: auto;
  `,
  progressCircleIcon: css` 
    font-size: 30px;
  `,
  progressText:       css`
    color: ${COLORS.GREEN};
    margin-top: 5px;
  `,
  link:               css`
    margin-top: auto;
    min-width: 102px; 
    font-size: 12px;
    align-self: flex-end;
  `,
};


type Props = {
  routes: Routes;
  plan: DevPlan | undefined;
  projectId: number;
  className?: string;
};


/**
 * DevPlanSummary: a component that renders the given dev plan state along with links to pages to adjust/complete dev
 * plans. This summary seen mainly on the dashboard
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevPlanSummary: React.FC<Props> = props => {
  const previousDevPlan    = (props.plan?.devPlanProjectId || props.projectId) < props.projectId
  const devPlanInteractive = props.plan?.developmentPlanUseInteractive;
  const devPlanStaticUrl   = props.plan?.developmentPlanPageUrl;

  const title = useMsgText(previousDevPlan ? Str.ReviewDevPlan : Str.DevPlan);

  const staticDevPlanUrl = React.useMemo(
    () => {
      const staticUrl = devPlanStaticUrl || '';
      if (staticUrl === '') {
        return '#';
      }
      if (staticUrl.toLowerCase().startsWith('http')) {
        return staticUrl
      }
      const baseUrl = BaseUrl.substring(0, BaseUrl.length - 4); // ends with '/api'
      const lcUrl = staticUrl.toLowerCase();

      return lcUrl.startsWith('developmentplans/') || lcUrl.startsWith('resources/')
        ? pathCombine(baseUrl, staticUrl)
        : pathCombine(baseUrl, 'DevelopmentPlans/', staticUrl)
    },
    [devPlanStaticUrl],
  );

  const linkRoute = React.useMemo(
    () => devPlanInteractive ? routeForProgress(props.plan, props.routes) : staticDevPlanUrl,
    [props.plan, props.routes, devPlanInteractive, staticDevPlanUrl],
  );

  const linkMsg   = React.useMemo(() => ctaMsgForProgress(props.plan), [props.plan]);
  const statusMsg = React.useMemo(() => subtitleMsgForProgress(props.plan), [props.plan]);

  const reqMsg  = React.useMemo(() => requirementsMsgFor(props.plan), [props.plan]);
  const reqSubs = React.useMemo(() => requirementsMsgSubstitutionsFor(props.plan), [props.plan]);

  const color      = React.useMemo(() => colorForProgress(props.plan), [props.plan]);
  const colorStyle = React.useMemo(() => css`color: ${color};`, [color]);

  const isTrackingGoals = React.useMemo(() => props.plan?.developmentPlanGoalsToTrack !== 0, [props.plan]);
  
  const { onIe }      = useBrowser();


  if (!props.plan) {
    return <div className={props.className} />;
  }

  return (
    <Card className={props.className}>
      <CardHeader title={title} css={styles.header} />
      {
        devPlanInteractive && !previousDevPlan
        ? (
          <LocalDiv css={[FLEX_ROW, styles.content]}>
            { // Left Side
              isTrackingGoals && (
                <Link to={linkRoute} css={[FLEX_COLUMN, styles.leftSide]}>
                  <ProgressCircle
                    ringConfig={[
                      {
                        name:     'progress',
                        progress: props.plan.devPlanAmountComplete,
                        color:    color,
                      },
                    ]}
                    radius={60}
                    stroke={12}
                    css={styles.progress}
                  >
                    <FontAwesomeIcon icon={faWrench} css={[styles.progressCircleIcon, colorStyle]} />
                  </ProgressCircle>
                  <Msg str={props.plan.devPlanStatusMessageKey} css={[styles.progressText, colorStyle]} />
                </Link>
              )
            }
            { // Space
              isTrackingGoals && <Spacer />
            }
            {/* Right Side */}
            <div css={[styles.rightSide, onIe ? styles.fullWidth : undefined]}>
              <Msg str={statusMsg} css={styles.subtitle} />
              {
                isTrackingGoals && <Msg str={reqMsg || ''} replace={reqSubs} css={styles.reqText} />
              }
              <Link to={linkRoute} css={[styles.link, btnStyle.primary(color)]}>
                <Msg str={linkMsg} />
              </Link>
            </div>
          </LocalDiv>
          )
        : (
          devPlanInteractive
          ? <LocalDiv css={[FLEX_ROW, styles.content]}>
              <div css={[styles.rightSide, onIe ? styles.fullWidth : undefined]}>
                <Msg str={previousDevPlan ? Str.DashDevPlanDoneMsg : Str.DevPlanAccessMsg} css={styles.subtitle} />
                <Link 
                  to={linkRoute}
                  target={previousDevPlan ? '#' : '_blank'}
                  css={[
                    styles.link,
                    btnStyle.primary(previousDevPlan ? COLORS.BLUE : COLORS.GREEN),
                  ]}
                >
                  <Msg str={previousDevPlan ? Str.Review : Str.DevPlan} />
                </Link>
              </div>
            </LocalDiv>
          : <LocalDiv css={[FLEX_ROW, styles.content]}>
              <div css={[styles.rightSide, onIe ? styles.fullWidth : undefined]}>
                <Msg str={previousDevPlan ? Str.DashDevPlanDoneMsg : Str.DevPlanAccessMsg} css={styles.subtitle} />
                <a 
                  href={linkRoute}
                  css={[styles.link, btnStyle.primary(COLORS.GREEN)]}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                >
                  <Msg str={previousDevPlan ? Str.Review : Str.DevPlan} />
                </a>
              </div>
            </LocalDiv>
        )
      }
    </Card>
  );
};

export default DevPlanSummary;
